/**
 * Is this string an address that could actually receive mail?
 *
 * ONE implementation, because the check exists on both sides of `/api/emails/add`
 * and they were separately wrong in the same way: each tested
 * `email.includes('@')`, so `tester@` enabled the "Send code" button AND passed
 * the server, which answered `{ ok: true }`. In development that returns a
 * `devCode` and looks fine; in production the user is told a code was sent to an
 * address that cannot receive one, and waits for mail that will never arrive.
 *
 * That is the same silent-failure shape as #610 — a flow that reports success
 * for work it did not do — on the page whose whole job is proving you control an
 * inbox.
 *
 * `z.email()` rather than a hand-rolled pattern, deliberately: Better Auth
 * validates signup with the same check, so an address accepted here is one the
 * rest of the product will also accept. A bespoke regex would drift from that,
 * and email grammar is not something to re-derive from memory.
 *
 * NORMALISE, THEN VALIDATE, AND SEND THE NORMALISED VALUE. The first cut
 * validated `value.trim()` and returned a boolean, so both callers passed the
 * RAW string on — and the repository only lowercases, it does not trim (contrary
 * to what this comment used to claim). A pasted `"  a@b.com  "` therefore passed
 * the gate and was STORED with its padding. `user_emails_email_lower_idx` is
 * UNIQUE on `lower(email)`, so the padded value is a different key: it can never
 * collide with the canonical row, and every reader that resolves an address
 * (`findByEmail`, `findVerifiedClaim`, share-principal, email-keyed membership
 * joins) compares against a canonical value and will never match it. The user
 * sees the address listed and verified while it grants nothing and receives
 * nothing.
 *
 * So `normalizeEmailAddress` exists and the callers use ITS result. A validator
 * that cleans a value and then throws the clean copy away invites exactly that.
 */
import { z } from 'zod'

const ADDRESS = z.email()

/** The canonical form: trimmed. Lowercasing stays with the repository, which
 *  already owns it on write. */
export function normalizeEmailAddress(value: string | null | undefined): string {
  return (value ?? '').trim()
}

export function isValidEmailAddress(value: string | null | undefined): boolean {
  return ADDRESS.safeParse(normalizeEmailAddress(value)).success
}

/**
 * What a user sees when a signup is refused because the address already belongs
 * to another account (#351).
 *
 * IT LIVES HERE, not beside the guard that throws it, because the SPA needs the
 * same string. Better Auth's social callback reports failure as
 * `?error=<message with spaces replaced by underscores>`, so `/login` has to
 * recognise this exact sentence to map it back to copy — and when the two were
 * separate, changing the wording on the server left the client keyed to the old
 * one, silently downgrading a specific message to the generic fallback. One
 * string, two readers, no drift.
 *
 * IT STATES A FACT AND PROMISES NOTHING. An earlier version ended "Sign in with
 * it instead". Password and email-code sign-in do resolve a verified secondary to
 * its owner (#626, `resolveVerifiedSecondary` in @papan/auth) — but the same
 * sentence is rendered by the Google flow, where the user may have no password
 * and no code was sent, and "instead" is not a promise every surface can keep
 * (email-code refuses an owner whose main address is unconfirmed). Telling
 * someone to do the thing that may just refuse them is worse than telling them
 * only what happened.
 *
 * English-only, like every other server-thrown auth message; see CLAUDE.md on
 * server-side i18n.
 */
export const EMAIL_CLAIMED_BY_ANOTHER_ACCOUNT =
  'This email address already belongs to an account.'

/** The same sentence as Better Auth's social callback encodes it. */
export const EMAIL_CLAIMED_OAUTH_CODE =
  EMAIL_CLAIMED_BY_ANOTHER_ACCOUNT.split(' ').join('_')
